import Drawer from '@/components/Drawer';
import { useSubmitOnEnter } from '@/components/Form/useSubmitOnEnter';
import { EMAIL_CENTER_ACTIONS } from '@/constants/actions';
import * as EmailCenterSelectors from '@/selectors/emailCenterSelectors';
import {
  createMailerRequest,
  updateMailerRequest,
} from '@/services/api/emailCenter';
import { MailerModel, MailerTransport } from '@/typings/api/emailCenter';
import { applyServerErrorsToForm, normalizeApiError } from '@/utils/apiErrors';
import { F, getIntlTranslation, INTL_ACTION, INTL_ENTITY } from '@/utils/intl';
import { processAPIResponseNow } from '@/utils/request';
import { Alert, Button, Form, Input, InputNumber, Select, Space } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useIntl, useSelector } from 'umi';

import { useEmailCenterValidateMessages } from '../../../useValidateMessages';
import styles from '../settings.less';

// Only SMTP is offered when creating: the other transports need provider
// configuration we do not support yet. Existing mailers keep their transport.
const CREATABLE_TRANSPORTS: MailerTransport[] = ['smtp'];
const SMTP_ENCRYPTIONS = ['tls', 'ssl'];

const MailerDrawer: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const validateMessages = useEmailCenterValidateMessages();
  const [form] = Form.useForm();

  const drawer = useSelector(EmailCenterSelectors.selectDrawer('mailer'));
  const journeyHashId = useSelector(
    EmailCenterSelectors.selectSelectedJourneyHashId,
  );
  const [saving, setSaving] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);

  const record = drawer.record as MailerModel | null;
  const isEdit = drawer.mode === 'edit' && record != null;
  const transport = Form.useWatch('transport', form);

  useEffect(() => {
    if (!drawer.visible) return;
    setGeneralError(null);
    if (isEdit && record) {
      // Credentials are write-only: the API never returns config, so SMTP
      // fields start empty and are sent only when (re-)entered.
      form.setFieldsValue({
        name: record.name,
        transport: record.transport,
      });
    } else {
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawer.visible, record?.id]);

  const close = useCallback(() => {
    dispatch({
      type: EMAIL_CENTER_ACTIONS.CLOSE_DRAWER,
      payload: { drawer: 'mailer' },
    });
  }, [dispatch]);

  const handleSave = useCallback(async () => {
    const values = await form.validateFields();

    const config =
      values.transport === 'smtp'
        ? {
            host: values.host,
            port: values.port,
            username: values.username,
            ...(values.password ? { password: values.password } : {}),
            ...(values.encryption ? { encryption: values.encryption } : {}),
          }
        : undefined;

    const payload: Record<string, unknown> = {
      name: values.name,
      transport: values.transport,
    };

    /*
     * Creating your first mailer and then having to go and switch it on is a
     * step with only one sensible answer, so the journey comes along and the
     * API takes it — but only for channels that have no mailer at all. A
     * channel already pointing at one configured in Mailcoach is left alone.
     */
    if (!isEdit && journeyHashId) {
      payload.journey = journeyHashId;
    }
    if (
      config &&
      Object.values(config).some((entry) => entry !== undefined && entry !== '')
    ) {
      payload.config = config;
    }

    setSaving(true);
    setGeneralError(null);
    const result = isEdit
      ? await updateMailerRequest({ params: { uuid: record!.id }, payload })
      : await createMailerRequest({ payload: payload as any });
    setSaving(false);

    processAPIResponseNow(result, {
      showSuccessMessage: true,
      successEntity: INTL_ENTITY.MAILER,
      onSuccess: () => {
        close();
        dispatch({ type: EMAIL_CENTER_ACTIONS.FETCH_MAILERS });
      },
      onError: (data) => {
        const normalized = normalizeApiError(data);
        applyServerErrorsToForm(form, normalized.fieldErrors, {
          // Server keys arrive as config.host etc. — map them onto the flat form.
          fieldMap: {
            'config.host': ['host'],
            'config.port': ['port'],
            'config.username': ['username'],
            'config.password': ['password'],
            'config.encryption': ['encryption'],
          },
          onUnmapped: (key, messages) =>
            setGeneralError((prev) => prev ?? `${key}: ${messages.join(' ')}`),
        });
        if (Object.keys(normalized.fieldErrors).length === 0) {
          setGeneralError(normalized.message);
        }
      },
    });
  }, [form, isEdit, record, close, dispatch, journeyHashId]);

  const isSmtp = transport === 'smtp';
  const transportOptions = useMemo(
    () =>
      (isEdit && record && !CREATABLE_TRANSPORTS.includes(record.transport)
        ? [...CREATABLE_TRANSPORTS, record.transport]
        : CREATABLE_TRANSPORTS
      ).map((entry) => ({ value: entry, label: entry.toUpperCase() })),
    [isEdit, record],
  );

  const submitOnEnter = useSubmitOnEnter(handleSave, { enabled: !saving });

  return (
    <Drawer
      width={480}
      open={drawer.visible}
      onOpenChange={close}
      title={
        <F
          entity={INTL_ENTITY.MAILER}
          action={isEdit ? INTL_ACTION.EDIT : INTL_ACTION.CREATE}
        />
      }
      footer={
        <Space className={styles.drawerFooter}>
          <Button onClick={close}>
            <F id="common.actions.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="primary" loading={saving} onClick={handleSave}>
            <F id="common.actions.save" defaultMessage="Save" />
          </Button>
        </Space>
      }
    >
      {generalError && (
        <Alert
          className={styles.generalError}
          type="error"
          showIcon
          message={generalError}
        />
      )}

      <Form
        {...submitOnEnter}
        validateMessages={validateMessages}
        form={form}
        layout="vertical"
        initialValues={{ transport: 'smtp' }}
      >
        <Form.Item
          name="name"
          label={<F id="common.attribute.name" defaultMessage="Name" />}
          rules={[{ required: true }, { max: 255 }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="transport"
          label={
            <F
              id="pages.emailCenter.settings.mailers.transport"
              defaultMessage="Transport"
            />
          }
          rules={[{ required: true }]}
        >
          <Select
            disabled={transportOptions.length < 2}
            options={transportOptions}
          />
        </Form.Item>

        {isSmtp && (
          <>
            <Form.Item
              name="host"
              label={
                <F
                  id="pages.emailCenter.settings.mailers.host"
                  defaultMessage="Host"
                />
              }
              rules={[{ required: !isEdit }]}
            >
              <Input placeholder="smtp.example.com" />
            </Form.Item>
            <Form.Item
              name="port"
              label={
                <F
                  id="pages.emailCenter.settings.mailers.port"
                  defaultMessage="Port"
                />
              }
              rules={[{ required: !isEdit }]}
            >
              <InputNumber min={1} max={65535} placeholder="587" />
            </Form.Item>
            <Form.Item
              name="username"
              label={
                <F
                  id="pages.emailCenter.settings.mailers.username"
                  defaultMessage="Username"
                />
              }
              rules={[{ required: !isEdit }]}
            >
              <Input autoComplete="off" />
            </Form.Item>
            <Form.Item
              name="password"
              label={
                <F
                  id="pages.emailCenter.settings.mailers.password"
                  defaultMessage="Password"
                />
              }
              rules={[{ required: !isEdit }]}
              tooltip={
                isEdit ? (
                  <F
                    id="pages.emailCenter.settings.mailers.passwordHint"
                    defaultMessage="Leave empty to keep the current password."
                  />
                ) : undefined
              }
            >
              <Input.Password
                autoComplete="new-password"
                placeholder={
                  isEdit
                    ? getIntlTranslation(intl, {
                        id: 'pages.emailCenter.settings.mailers.passwordUnchanged',
                        defaultMessage: '(unchanged)',
                      })
                    : undefined
                }
              />
            </Form.Item>
            <Form.Item
              name="encryption"
              label={
                <F
                  id="pages.emailCenter.settings.mailers.encryption"
                  defaultMessage="Encryption"
                />
              }
            >
              <Select
                allowClear
                options={SMTP_ENCRYPTIONS.map((entry) => ({
                  value: entry,
                  label: entry.toUpperCase(),
                }))}
              />
            </Form.Item>
          </>
        )}
      </Form>
    </Drawer>
  );
};

export default MailerDrawer;
